<template>
  <div
    class="ola-menu-item"
    :class="{ 
      'ola-menu-item-active': active,
      'ola-menu-item-disabled': disabled
    }"
    :style="activeStyle"
  >
    <a 
      v-if="href" 
      :href="href" 
      :target="target"
      class="ola-menu-item-link"
      @click="handleClick"
    >
      <slot name="icon"></slot>
      <span class="ola-menu-item-content" :class="{ 'ola-menu-item-content-active': isCurrentRoutePage }">
        <slot></slot>
      </span>
    </a>
    <div
      v-else
      class="ola-menu-item-wrapper"
      @click="handleClick"
    >
      <slot name="icon"></slot>
      <span class="ola-menu-item-content" :class="{ 'ola-menu-item-content-active': isCurrentRoutePage }">
        <slot></slot>
      </span>
    </div>
  </div>
</template>

<script>
export default {
  name: 'OlaMenuItem',
  props: {
    // 菜单项索引
    index: {
      type: String,
      required: true
    },
    // 是否禁用
    disabled: {
      type: Boolean,
      default: false
    },
    // 路由模式下的链接
    route: {
      type: [String, Object],
      default: null
    },
    // 链接地址，用于SEO友好的a标签
    href: {
      type: String,
      default: ''
    },
    // 链接打开方式
    target: {
      type: String,
      default: '_self'
    },
    // 路径匹配模式，用于确定当前菜单项是否激活
    pathPattern: {
      type: [String, RegExp],
      default: null
    }
  },
  inject: ['rootMenu'],
  data() {
    return {
      isRouteActive: false
    };
  },
  computed: {
    active() {
      // 优先使用路由匹配结果，如果没有路由匹配则使用index匹配
      return this.isRouteActive || this.rootMenu.activeIndex === this.index;
    },
    isCurrentRoutePage() {
      if (!this.$route || !this.$router) return false;

      const currentPath = this.$route.path;
      let targetLocation = null;

      if (this.pathPattern) {
        return false;
      }

      if (this.href) {
        const isInternal = this.href.startsWith('/') || !/^(https?|mailto|tel):/.test(this.href);
        if (isInternal) {
          targetLocation = this.href;
        }
      } else if (this.route) {
        targetLocation = this.route;
      }

      if (targetLocation === null) return false;
      
      try {
        const resolvedPath = this.$router.resolve(targetLocation).path;
        return resolvedPath === currentPath;
      } catch (e) {
        return false;
      }
    },
    activeStyle() {
      if (!this.active) return {};
      
      const style = {
        color: this.rootMenu.activeTextColor || '#409eff',
        backgroundColor: this.rootMenu.backgroundColor || '',
      };

      if (this.isCurrentRoutePage) {
        style.fontWeight = '500'; // 激活状态下加粗显示
      }
      
      return style;
    }
  },
  methods: {
    handleClick(event) {
      if (this.disabled) return;
      
      this.rootMenu.selectItem(this.index);
      this.$emit('click', this.index);
      
      // 路由跳转
      if (this.route && this.$router) {
        event.preventDefault(); // 阻止a标签默认行为
        this.$router.push(this.route);
      }
    },
    
    // 检查当前路由是否匹配
    checkRouteMatch() {
      if (!this.$route) return false;
      
      const currentPath = this.$route.path;
      
      // 如果有明确的路径匹配模式
      if (this.pathPattern) {
        if (this.pathPattern instanceof RegExp) {
          return this.pathPattern.test(currentPath);
        }
        return currentPath.startsWith(this.pathPattern);
      }
      
      // 使用href属性进行匹配
      if (this.href) {
        // 对于首页，只有当路径完全等于/时才匹配
        if (this.href === '/') {
          return currentPath === '/' || currentPath === '';
        }
        // 对于其他页面，只要路径以href开头就匹配
        return currentPath.startsWith(this.href);
      }
      
      // 使用route属性进行匹配
      if (this.route) {
        const routePath = typeof this.route === 'string' ? this.route : this.route.path;
        if (routePath) {
          if (routePath === '/') {
            return currentPath === '/' || currentPath === '';
          }
          return currentPath.startsWith(routePath);
        }
      }
      
      return false;
    },
    
    // 更新路由匹配状态
    updateRouteActiveState() {
      this.isRouteActive = this.checkRouteMatch();
    }
  },
  mounted() {
    this.rootMenu.addItem(this);
    
    // 初始检查路由匹配
    this.updateRouteActiveState();
    
    // 监听路由变化
    if (this.$router) {
      this.$watch('$route', () => {
        this.updateRouteActiveState();
      });
    }
    
    // 特殊处理：如果当前路由与href匹配，但不处理target="_blank"的情况
    if (this.$route && this.href && this.target !== '_blank') {
      const currentPath = this.$route.path;
      if (this.href === '/' && (currentPath === '/' || currentPath === '')) {
        this.isRouteActive = true;
      } else if (this.href !== '/' && currentPath.startsWith(this.href)) {
        this.isRouteActive = true;
      }
    }
  },
  beforeUnmount() {
    this.rootMenu.removeItem(this);
  }
};
</script>

<style>
.ola-menu-item {
  list-style: none;
  position: relative;
  transition: all .3s;
  white-space: nowrap;
}
.ola-menu-item:hover a{
  color: var(--header-main-color, #FF1F59);
}
.ola-menu-item-link,
.ola-menu-item-wrapper {
  height: 56px;
  line-height: 56px;
  font-size: 16px;
  color: #303133;
  padding: 0 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  text-decoration: none;
  width: 100%;
}

.ola-menu-item:hover .ola-menu-item-link,
.ola-menu-item:hover .ola-menu-item-wrapper {
  outline: none;
  /* background-color: #f5f7fa; */
}

.ola-menu-item-active .ola-menu-item-link,
.ola-menu-item-active .ola-menu-item-wrapper {
  color: #333;
  font-weight: 400;
}

.ola-menu-item-content-active {
  font-weight: 500 !important;
}

.ola-menu-item-disabled .ola-menu-item-link,
.ola-menu-item-disabled .ola-menu-item-wrapper {
  opacity: 0.25;
  cursor: not-allowed;
  pointer-events: none;
}

.ola-menu-item [class^=ola-icon] {
  margin-right: 8px;
  width: 24px;
  text-align: center;
  font-size: 18px;
  vertical-align: middle;
}

.ola-menu-item-content {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ola-menu-horizontal .ola-menu-item {
  float: left;
}

.ola-menu-horizontal .ola-menu-item-link,
.ola-menu-horizontal .ola-menu-item-wrapper {
  height: 63px;
  line-height: 63px;
  margin: 0;
  padding: 0 12px;
}



/* 子菜单中的菜单项样式 */
.ola-submenu-content .ola-menu-item-link,
.ola-submenu-content .ola-menu-item-wrapper {
  height: 40px;
  line-height: 40px;
  padding: 0 12px;
  font-size: 16px;
}

.ola-menu-horizontal .ola-submenu-content .ola-menu-item {
  float: none;
}

.ola-menu-horizontal .ola-submenu-content .ola-menu-item-link,
.ola-menu-horizontal .ola-submenu-content .ola-menu-item-wrapper {
  height: 36px;
  line-height: 36px;
  padding: 0 12px;
  border-bottom: none;
}

/* 折叠模式下的菜单项 */
.ola-menu-collapse .ola-menu-item-link,
.ola-menu-collapse .ola-menu-item-wrapper {
  padding: 0;
  height: 56px;
  line-height: 56px;
  text-align: center;
}

.ola-menu-collapse .ola-menu-item .ola-menu-item-content {
  display: none;
}

.ola-menu-collapse .ola-menu-item [class^=ola-icon] {
  margin: 0;
}

@media screen and (max-width: 763px) {
  .ola-menu-horizontal .ola-menu-item {
    width: 100%;
    float: none;
  }
  
  .ola-menu-horizontal .ola-menu-item-link,
  .ola-menu-horizontal .ola-menu-item-wrapper {
    height: 50px;
    line-height: 50px;
  }
}
</style> 